export const FIELD_TYPES = [
  { value: 'textbox', label: 'Textbox' },
  { value: 'dropdown', label: 'Dropdown' },
  { value: 'checklist', label: 'Checklist' },
  { value: 'date', label: 'Date' },
  { value: 'datetime', label: 'Date & Time' },
];

export const arrayMove = (items, fromIndex, toIndex) => {
  if (fromIndex === toIndex || fromIndex < 0 || toIndex < 0 || fromIndex >= items.length) return items;
  const next = [...items];
  const [moved] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, moved);
  return next;
};

export const sortByOrder = (items) => [...items].sort((left, right) => (left.order || 0) - (right.order || 0));

export const matchesSectionId = (field, section) => (
  field.sectionId === section.id || field.sectionId === section.clientId
);

export const getFieldsForSection = (fields, section) => sortByOrder(
  fields.filter((field) => matchesSectionId(field, section))
);

export const reorderFieldsGlobally = (sections, fields) => {
  let order = 0;
  const ordered = [];
  const processedIds = new Set();

  // For each section, pick fields that belong to it in the order they appear
  // in the input `fields` array. This preserves the caller's intended order
  // (e.g. after an arrayMove reorder) instead of re-sorting by stale `.order` values.
  sections.forEach((section) => {
    fields.forEach((field) => {
      if (!processedIds.has(field.clientId) && matchesSectionId(field, section)) {
        processedIds.add(field.clientId);
        ordered.push({ ...field, order: order++ });
      }
    });
  });

  // Append any orphaned fields not matched to any section
  fields.forEach((field) => {
    if (!processedIds.has(field.clientId)) {
      ordered.push({ ...field, order: order++ });
    }
  });

  return ordered;
};

export const computeFieldDropIndex = (sectionFields, sourceClientId, targetClientId, dropPosition = 'before') => {
  const fromIndex = sectionFields.findIndex((field) => field.clientId === sourceClientId);

  if (targetClientId === '__end__') {
    // Dropping at end — if field is already in this section, put it last
    if (fromIndex >= 0) return sectionFields.length - 1;
    // If field is from another section, it will be appended
    return sectionFields.length;
  }

  const targetIndex = sectionFields.findIndex((field) => field.clientId === targetClientId);
  if (targetIndex < 0) return -1;

  // For same-section moves, arrayMove(arr, from, to) removes the item at `from`
  // then inserts at `to` in the resulting array. We want:
  //   - 'before' the target: place at target's current visual position
  //   - 'after' the target: place right after target's current visual position
  //
  // When fromIndex < targetIndex, removing the source shifts the target down by 1,
  // so we must subtract 1 from the raw target position.
  // When fromIndex > targetIndex, removing the source doesn't affect positions before it.

  if (fromIndex >= 0) {
    // Same-section reorder
    if (dropPosition === 'after') {
      // We want the field to end up after the target.
      // If dragging downward (from < target): after removal, target shifts to targetIndex-1,
      // so inserting at targetIndex puts us right after the target.
      // If dragging upward (from > target): target stays at targetIndex,
      // so inserting at targetIndex+1 puts us right after the target.
      return fromIndex < targetIndex ? targetIndex : targetIndex + 1;
    }
    // dropPosition === 'before'
    // We want the field to end up before the target.
    // If dragging downward (from < target): after removal, target shifts to targetIndex-1,
    // so inserting at targetIndex-1 puts us right before the target.
    // If dragging upward (from > target): target stays at targetIndex,
    // so inserting at targetIndex puts us right before the target.
    return fromIndex < targetIndex ? targetIndex - 1 : targetIndex;
  }

  // Cross-section: no adjustment needed, just raw position
  return dropPosition === 'after' ? targetIndex + 1 : targetIndex;
};

/**
 * Move a field across sections. Returns the updated fields array.
 * `sourceClientId` — the field being dragged
 * `targetSection` — the section it's being dropped into
 * `targetClientId` — the field it's being dropped onto (or '__end__')
 * `dropPosition` — 'before' or 'after'
 */
export const moveFieldAcrossSections = (allFields, sections, sourceClientId, targetSection, targetClientId, dropPosition = 'before') => {
  const sourceField = allFields.find((f) => f.clientId === sourceClientId);
  if (!sourceField) return null;

  const newSectionId = targetSection.id || targetSection.clientId;

  // Remove the field from its current position
  const fieldsWithoutSource = allFields.filter((f) => f.clientId !== sourceClientId);

  // Update the field's sectionId
  const movedField = { ...sourceField, sectionId: newSectionId };

  // Get the target section's fields (without the moved field)
  const targetSectionFields = getFieldsForSection(fieldsWithoutSource, targetSection);

  // Calculate insert position
  let insertIndex;
  if (targetClientId === '__end__' || targetSectionFields.length === 0) {
    insertIndex = targetSectionFields.length;
  } else {
    const targetIndex = targetSectionFields.findIndex((f) => f.clientId === targetClientId);
    insertIndex = targetIndex >= 0
      ? (dropPosition === 'after' ? targetIndex + 1 : targetIndex)
      : targetSectionFields.length;
  }

  // Insert the moved field at the right position in the target section
  const newTargetFields = [...targetSectionFields];
  newTargetFields.splice(insertIndex, 0, movedField);

  // Rebuild: all fields not in the target section + the new target section fields
  const targetFieldIds = new Set(targetSectionFields.map((f) => f.clientId));
  const otherFields = fieldsWithoutSource.filter((f) => !targetFieldIds.has(f.clientId));
  const combinedFields = [...otherFields, ...newTargetFields];

  return reorderFieldsGlobally(sections, combinedFields);
};

export const createDragPayload = (type, id) => JSON.stringify({ type, id });

export const parseDragPayload = (rawValue) => {
  if (!rawValue) return null;
  try {
    const parsed = JSON.parse(rawValue);
    if (parsed?.type && parsed?.id) return parsed;
  } catch {
    // Legacy plain-text drag ids.
  }
  return { type: 'legacy', id: rawValue };
};
